import { GardenArea, GardenPlant } from '../services/gardenService';

const PADDING = 20;
const TOP_PADDING = 120; // Extra padding at top for year display
const SPACING = 50;

export interface EmptyCell {
  dayOfYear: number;
  position: { x: number; y: number };
}

/**
 * Get the day of year (1-365 or 1-366) for a given date
 */
function getDayOfYear(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

/**
 * Check if a year is a leap year
 */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/**
 * Get the number of days in a year
 */
function getDaysInYear(year: number): number {
  return isLeapYear(year) ? 366 : 365;
}

/**
 * Calculate position for a day of year based on grid layout
 * @param area - Garden area
 * @param dayOfYear - Day of year (1-365 or 1-366)
 * @returns Position coordinates {x, y}
 */
function calculatePositionFromDayOfYear(area: GardenArea, dayOfYear: number): { x: number; y: number } {
  const availableWidth = area.width - (PADDING * 2);
  const daysPerRow = Math.max(1, Math.floor(availableWidth / SPACING));
  const dayIndex = dayOfYear - 1; // Convert to 0-based index
  const row = Math.floor(dayIndex / daysPerRow);
  const col = dayIndex % daysPerRow;

  const relativeX = PADDING + (col * SPACING) + (SPACING / 2);
  const relativeY = TOP_PADDING + (row * SPACING) + (SPACING / 2);

  const maxRelativeX = area.width - PADDING;
  
  const clampedX = Math.max(PADDING, Math.min(relativeX, maxRelativeX));

  const x = area.position_x + clampedX;
  const y = area.position_y + relativeY;

  return { x, y };
}

/**
 * Calculate the required height for a garden area based on the number of days in the year.
 * @param areaWidth The width of the area.
 * @param year The year number.
 * @returns The calculated height.
 */
export function calculateRequiredHeight(areaWidth: number, year: number): number {
  const daysInYear = getDaysInYear(year);
  const availableWidth = areaWidth - PADDING * 2;
  const daysPerRow = Math.max(1, Math.floor(availableWidth / SPACING));
  const numRows = Math.ceil(daysInYear / daysPerRow);
  const totalHeight = TOP_PADDING + (numRows * SPACING) + PADDING;
  return totalHeight;
}

/**
 * Calculate positions for all plants grouped by area, based on day of year
 * @param areas - All garden areas
 * @param plants - All plants
 * @returns Map of plant ID to calculated position
 */
export function calculateAllPlantPositions(
  areas: GardenArea[],
  plants: GardenPlant[]
): Map<number, { x: number; y: number }> {
  const positionMap = new Map<number, { x: number; y: number }>();

  areas.forEach(area => {
    if (!area.year_number) return;

    const plantsInArea = plants.filter(plant => 
      plant.plant_date && new Date(plant.plant_date).getFullYear() === area.year_number
    );

    // Calculate position for each plant based on its day of year
    plantsInArea.forEach((plant) => {
      if (!plant.plant_date) return;
      
      const plantDate = new Date(plant.plant_date);
      const dayOfYear = getDayOfYear(plantDate);
      const position = calculatePositionFromDayOfYear(area, dayOfYear);
      positionMap.set(plant.id, position);
    });
  });

  return positionMap;
}

/**
 * Calculate positions for empty cells (days without plants) for each area
 * @param areas - All garden areas
 * @param plants - All plants
 * @returns Map of area ID to array of empty cell positions
 */
export function calculateEmptyCellPositions(
  areas: GardenArea[],
  plants: GardenPlant[]
): Map<number, EmptyCell[]> {
  const emptyCellsMap = new Map<number, EmptyCell[]>();

  areas.forEach(area => {
    if (!area.year_number) return;

    const year = area.year_number;
    const daysInYear = getDaysInYear(year);
    
    // Get all plants in this area with their day of year
    const plantsInArea = plants
      .filter(plant => 
        plant.plant_date && new Date(plant.plant_date).getFullYear() === year
      )
      .map(plant => {
        const plantDate = new Date(plant.plant_date!);
        return getDayOfYear(plantDate);
      });

    // Create a set of days that have plants
    const daysWithPlants = new Set(plantsInArea);

    // Generate empty cells for all days without plants
    const emptyCells: EmptyCell[] = [];
    for (let dayOfYear = 1; dayOfYear <= daysInYear; dayOfYear++) {
      if (!daysWithPlants.has(dayOfYear)) {
        const position = calculatePositionFromDayOfYear(area, dayOfYear);
        emptyCells.push({
          dayOfYear,
          position
        });
      }
    }

    emptyCellsMap.set(area.id, emptyCells);
  });

  return emptyCellsMap;
}

